import axios from "const/axios";

const STORAGE_KEY = "clientDetailsFormConfig";

// Defaults are modeled after the standard "Client Details" intake form
// (matches the Google Form template). Admin can reorder, toggle, add or
// remove fields from Settings → Client Details Form.
const DEFAULT_FIELDS = [
  { id: "name", label: "Full Name", type: "text", required: true, enabled: true, layout: "half" },
  {
    id: "email",
    label: "Email Address",
    type: "email",
    required: true,
    enabled: true,
    layout: "half",
  },
  {
    id: "number",
    label: "Contact Number",
    type: "tel",
    required: true,
    enabled: true,
    layout: "half",
  },
  {
    id: "gender",
    label: "Gender",
    type: "select",
    required: true,
    enabled: true,
    layout: "half",
    options: ["Male", "Female", "Other"],
  },
  {
    id: "address",
    label: "Address",
    type: "text",
    required: false,
    enabled: true,
    layout: "half",
  },
  {
    id: "nationality",
    label: "Country of Passport",
    type: "text",
    required: false,
    enabled: true,
    layout: "half",
  },
  {
    id: "visaStatus",
    label: "Visa Type",
    type: "text",
    required: false,
    enabled: true,
    layout: "half",
  },
  {
    id: "visaExpiry",
    label: "Visa Expiry",
    type: "date",
    required: false,
    enabled: true,
    layout: "half",
  },
  {
    id: "dob",
    label: "Date of Birth",
    type: "date",
    required: true,
    enabled: true,
    layout: "half",
  },
  {
    id: "maritalStatus",
    label: "Marital Status",
    type: "select",
    required: false,
    enabled: true,
    layout: "half",
    options: ["Single", "Married", "Divorced", "Other"],
  },

  {
    id: "passportNumber",
    label: "Passport Number",
    type: "text",
    required: true,
    enabled: true,
    layout: "half",
  },
  {
    id: "dateOfIssue",
    label: "Passport Date of Issue",
    type: "date",
    required: true,
    enabled: true,
    layout: "half",
  },
  {
    id: "passportExpiry",
    label: "Passport Date of Expiry",
    type: "date",
    required: true,
    enabled: true,
    layout: "half",
  },
  {
    id: "usiNumber",
    label: "USI Number",
    type: "text",
    required: false,
    enabled: true,
    layout: "half",
  },

  {
    id: "australianAddress",
    label: "Australian Address",
    type: "textarea",
    required: true,
    enabled: true,
    layout: "full",
  },
  {
    id: "homeCountryAddress",
    label: "Home Country Address",
    type: "textarea",
    required: true,
    enabled: true,
    layout: "full",
  },

  {
    id: "currentCourseProvider",
    label: "Current Course Provider",
    type: "text",
    required: true,
    enabled: true,
    layout: "half",
  },
  { id: "intake", label: "Intake", type: "text", required: false, enabled: true, layout: "half" },

  {
    id: "emergencyContactName",
    label: "Emergency Contact Name",
    type: "text",
    required: true,
    enabled: true,
    layout: "half",
  },
  {
    id: "emergencyRelation",
    label: "Emergency Relationship",
    type: "text",
    required: true,
    enabled: true,
    layout: "half",
  },
  {
    id: "emergencyContact",
    label: "Emergency Contact Number",
    type: "tel",
    required: true,
    enabled: true,
    layout: "half",
  },
  {
    id: "emergencyEmail",
    label: "Emergency Email Address",
    type: "email",
    required: true,
    enabled: true,
    layout: "half",
  },
  {
    id: "interestedServices",
    label: "Interested Services",
    type: "checkbox-group",
    required: false,
    enabled: true,
    layout: "full",
    options: [
      "Admission",
      "Visa",
      "Consultation",
      "RPL Certification",
      "Skills Assessment",
      "Insurance",
      "Other",
    ],
  },
  {
    id: "source",
    label: "How did you hear about us?",
    type: "select",
    required: false,
    enabled: true,
    layout: "half",
    options: ["Google", "Facebook", "Tiktok", "Instagram", "Family or Friends.", "Other"],
  },
  {
    id: "comments",
    label: "Comments",
    type: "textarea",
    required: false,
    enabled: true,
    layout: "full",
  },
];

const normalizeConfig = (storedConfig) => {
  const defaultById = new Map(DEFAULT_FIELDS.map((field) => [field.id, field]));
  const used = new Set();
  const normalized = [];
  const customFields = [];

  if (Array.isArray(storedConfig)) {
    storedConfig.forEach((item) => {
      if (!item || !item.id) return;
      if (used.has(item.id)) return; // deduplicate
      if (defaultById.has(item.id)) {
        const base = defaultById.get(item.id);
        normalized.push({ ...base, ...item, options: item.options || base.options });
        used.add(item.id);
      } else if (item.isCustom) {
        customFields.push(item);
      }
    });
  }

  DEFAULT_FIELDS.forEach((field) => {
    if (!used.has(field.id)) normalized.push({ ...field });
  });

  return [...normalized, ...customFields];
};

export const getDefaultClientDetailsFormConfig = () =>
  DEFAULT_FIELDS.map((field) => ({ ...field }));

/** Turns a stored config (the API's fieldsJson) into a renderable list, defaults when absent. */
export const parseClientDetailsFormConfig = (fieldsJson) => {
  if (!fieldsJson) return getDefaultClientDetailsFormConfig();
  try {
    return normalizeConfig(JSON.parse(fieldsJson));
  } catch (error) {
    return getDefaultClientDetailsFormConfig();
  }
};

export const getClientDetailsFormConfig = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return getDefaultClientDetailsFormConfig();
    return normalizeConfig(JSON.parse(raw));
  } catch (error) {
    return getDefaultClientDetailsFormConfig();
  }
};

export const setClientDetailsFormConfig = (config) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    // Ignore write errors so the UI keeps working in private/locked storage modes.
  }
};

// Helper used by the share modal / navbar to build the public form URL
// from the existing public-link slug stored in context (which is the
// full check-in URL: <origin>/client/check-in/<slug>).
export const buildClientDetailsUrl = (publicCheckInUrl) => {
  if (!publicCheckInUrl) return "";
  const slug = publicCheckInUrl.split("/").filter(Boolean).pop();
  if (!slug) return "";
  return `${window.location.origin}/client/details/${slug}`;
};

export const fetchClientDetailsFormConfigFromAPI = async () => {
  const res = await axios.get("/client-details-form/config");
  const fieldsJson = res?.data?.data?.fieldsJson;
  if (!fieldsJson) {
    // API has no saved config — fall back to localStorage, then defaults
    return getClientDetailsFormConfig();
  }
  try {
    return normalizeConfig(JSON.parse(fieldsJson));
  } catch {
    return getClientDetailsFormConfig();
  }
};

/**
 * A value stored under a field the form no longer shows: a custom field, or one an admin has
 * since removed or disabled. It stays on the record, so it has to stay on the screen.
 */
export const labelForUnlistedField = (id) =>
  String(id)
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

export const saveClientDetailsFormConfigToAPI = async (config) => {
  await axios.put("/client-details-form/config", {
    fieldsJson: JSON.stringify(config),
  });
};
